import { validationProblemsTag } from '../../../apis/apiValidationProblems';
import {
  ApiOperationType,
  BrandConfigurationResponse,
  Condition,
  LogicalOperator,
  Operation,
  ProductResponse,
  SortDirection,
  Sorting,
  ValidationArea,
} from '@ydistri/api-sdk';
import { apiSlice, TemplateOrCalculation } from '../../../apis/api';
import {
  CalculationsCollection,
  ProductsCollection,
  TemplatesCollection,
} from '../../../swagger/collections';
import { SignalConfigurationsChanged } from '../../../signalr/signalrInterfaces';
import { signalrClient } from '../../../signalr/client';
import {
  forceRefetchForInfiniteScroll,
  mergeForInfiniteScroll,
  serializeQueryArgsForInfiniteScroll,
} from '@ydistri/ds';
import { getTags } from '../../../apis/apiLib';
import { getSelectedProjectShortName } from '../../../apis/apiHeader';

const { TAGS, TAGS_ARRAY } = getTags('brandsConfiguration');

export interface UpdateBrandConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  brandId: number;
}

export interface GetBrandConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  top?: number;
  skip?: number;
  search?: string;
  pastedBrands?: string[];
}

export interface GetBrandProductsPayload {
  brandId: number;
  top?: number;
  skip?: number;
  search?: string;
}

export interface DeleteBrandConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  brandId: number;
}

export const apiBrands = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    getBrandConfiguration: builder.query<
      BrandConfigurationResponse[], //Expanded
      GetBrandConfigurationPayload
    >({
      queryFn: async payload => {
        const sortings: Sorting[] = [
          { fieldName: 'IsConfigured', direction: SortDirection.Desc },
          { fieldName: 'Name', direction: SortDirection.Asc },
        ];

        const conditions: Condition[] = [];

        if ((payload.pastedBrands ?? []).length > 0) {
          payload.pastedBrands?.forEach(pb => {
            conditions.push({
              fieldName: 'Name',
              value: pb,
              operation: Operation.Eq,
              logicalOperator: LogicalOperator.Or,
            });
          });
        } else {
          if (payload.search) {
            conditions.push({
              rawQuery: `Name contains ${payload.search}`,
            });
          }
        }

        const {
          data: { data: brandConfiguration },
        } = await (payload.templateOrCalculation.type === 'Template'
          ? TemplatesCollection.getTemplateBrands(payload.templateOrCalculation.id, {
              sortings,
              top: payload.top,
              skip: payload.skip ?? 0,
              conditions,
              inlineCount: true,
            })
          : CalculationsCollection.getCalculationBrands(payload.templateOrCalculation.id, {
              sortings,
              top: payload.top,
              skip: payload.skip ?? 0,
              conditions,
              inlineCount: true,
            }));

        return { data: brandConfiguration };
      },
      providesTags: (result, error, arg) => [
        { type: TAGS.brandsConfiguration, id: JSON.stringify(arg) },
      ],
      async onCacheEntryAdded({ templateOrCalculation: { type: _, id: selectedTemplateId } }, api) {
        try {
          await api.cacheDataLoaded;

          const listener = (signals: SignalConfigurationsChanged[]) => {
            // eslint-disable-next-line no-console -- we want this output
            console.log('CONFIGURATION CHANGED', signals);
            const brandIdsToDelete: number[] = [];
            signals.forEach(signal => {
              if (selectedTemplateId !== signal.templateId) return;
              const brandId = signal.entityId;
              if (signal.entityType === 'BrandConfiguration') {
                if (signal.operationType === ApiOperationType.Create) {
                  api.updateCachedData(draft => {
                    const brand = draft.find(r => r.id === brandId);
                    if (brand) brand.isConfigured = true;
                  });
                } else if (signal.operationType === ApiOperationType.Delete) {
                  brandIdsToDelete.push(brandId);
                }
              }
            });

            if (brandIdsToDelete.length > 0) {
              api.updateCachedData(draft => {
                draft.forEach(brand => {
                  const deleteBrand = brandIdsToDelete.includes(brand.id);
                  if (deleteBrand) {
                    if (brand.productCount > 0) {
                      brand.isConfigured = false;
                    }
                  }
                });
              });
            }
          };

          signalrClient.on('TemplateConfigurationsChanged', listener);

          api.cacheEntryRemoved.then(() => {
            signalrClient.off('TemplateConfigurationsChanged', listener);
          });
        } catch {
          // no-op in case cache entry was removed before data was loaded
        }
        await api.cacheEntryRemoved;
      },
      serializeQueryArgs: serializeQueryArgsForInfiniteScroll<GetBrandConfigurationPayload>(
        getSelectedProjectShortName,
      ),
      merge: (currentCache, newItems, otherArgs): void => {
        if (otherArgs.arg.skip === 0) {
          currentCache.splice(0, currentCache.length);
        }
        const itemsToPush = newItems.filter(i => !currentCache.find(c => c.id === i.id));
        currentCache.push(...itemsToPush);
      },
      forceRefetch: forceRefetchForInfiniteScroll<GetBrandConfigurationPayload | undefined>(),
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response from mutation
    updateBrandConfiguration: builder.mutation<void, UpdateBrandConfigurationPayload>({
      invalidatesTags: (result, error, { templateOrCalculation }) => [
        validationProblemsTag(templateOrCalculation, ValidationArea.Brands),
      ],
      queryFn: async payload => {
        const { data } = await TemplatesCollection.postTemplateBrandConfiguration(
          payload.templateOrCalculation.id,
          payload.brandId,
        );

        return { data };
      },
      onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiBrands.util.updateQueryData(
            'getBrandConfiguration',
            {
              templateOrCalculation: payload.templateOrCalculation,
            },
            draft => {
              const brand = draft.find(r => r.id === payload.brandId);
              if (brand)
                Object.assign(brand, {
                  isConfigured: true,
                });
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response from mutation
    deleteBrandConfiguration: builder.mutation<void, DeleteBrandConfigurationPayload>({
      invalidatesTags: (result, error, { templateOrCalculation }) => [
        validationProblemsTag(templateOrCalculation, ValidationArea.Brands),
      ],
      queryFn: async payload => {
        await TemplatesCollection.deleteTemplateBrandConfiguration(
          payload.templateOrCalculation.id,
          payload.brandId,
        );
        return { data: undefined };
      },
      onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiBrands.util.updateQueryData(
            'getBrandConfiguration',
            {
              templateOrCalculation: payload.templateOrCalculation,
            },
            draft => {
              const brand = draft.find(r => r.id === payload.brandId);
              if (brand)
                Object.assign(brand, {
                  isConfigured: false,
                });
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response from mutation
    resetBrandConfiguration: builder.mutation<void, TemplateOrCalculation>({
      invalidatesTags: (result, error, arg) => [validationProblemsTag(arg, ValidationArea.Brands)],
      queryFn: async payload => {
        await TemplatesCollection.deleteTemplateBrandConfigurations(payload.id);
        return { data: undefined };
      },
      onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiBrands.util.updateQueryData(
            'getBrandConfiguration',
            {
              templateOrCalculation: payload,
            },
            draft => {
              draft.forEach(b => {
                if (b.isConfigured) b.isConfigured = false;
              });
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    getBrandProducts: builder.query<
      ProductResponse[], //Expanded
      GetBrandProductsPayload
    >({
      queryFn: async payload => {
        const conditions = [
          {
            rawQuery: `Brand/Id eq ${payload.brandId}`,
          },
        ];

        const {
          data: { data: brandProducts },
        } = await ProductsCollection.productsList({
          top: payload.top,
          skip: payload.skip ?? 0,
          inlineCount: true,
          conditions: conditions,
          search: payload.search,
          sortings: [{ fieldName: 'Name', direction: SortDirection.Asc }],
        });

        return { data: brandProducts };
      },
      providesTags: (result, error, arg) => [{ type: TAGS.brandProducts, id: `bp-${arg.brandId}` }],
      serializeQueryArgs: serializeQueryArgsForInfiniteScroll<GetBrandProductsPayload>(
        getSelectedProjectShortName,
      ),
      merge: mergeForInfiniteScroll<ProductResponse, GetBrandProductsPayload>(),
      forceRefetch: forceRefetchForInfiniteScroll<GetBrandProductsPayload | undefined>(),
    }),
  }),
});

export const {
  useGetBrandConfigurationQuery,
  useUpdateBrandConfigurationMutation,
  useDeleteBrandConfigurationMutation,
  useGetBrandProductsQuery,
  useResetBrandConfigurationMutation,
} = apiBrands;
